import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { CachePolicies } from "use-http";

import { useGetImageQueries } from "api/services/useImageQueries";
import { STATUS } from "components/detector/Activity";
import { PageContext } from "components/detector/ImageQueryPreview";
import { JANZU_URL } from "config/config";
import { DETECTOR_DETAILS_REFRESH_TIME, EXPLORE_PAGE_REFRESH_TIME } from "config/constants";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { useFetchCustom } from "hooks/useFetchCustom";
import { fetchPosicheck, isFetchingDetector, setRequestFailed } from "store/slices/DetectorPage/detectorSlice";
import { ListResponse } from "types/api";
import { DetectorModes } from "types/createDetector";
import { Detector } from "types/detectors";
import { ImageQuery } from "types/imageQueries";
import { constructImageQueriesUrl } from "utils/imageQueriesUtils";
import { isExplorePage } from "utils/utilityFunctions";

export enum TabChoices {
  Activity = "detector-activity",
  Flagged = "flagged",
}

interface UseImageQueryPreviewProps {
  detector: Detector | undefined;
  count: number;
  columns: number;
  areExamples: boolean;
  pageContext: PageContext;
}

const MAX_ROWS_TO_FETCH = 3;

function useImageQueryPreview(props: UseImageQueryPreviewProps) {
  const { detector, count, columns, areExamples, pageContext } = props;

  const [selectedTab, setSelectedTab] = useState(TabChoices.Activity);
  const [relabeledPosicheckData, setRelabeledPosicheckData] = useState("");
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [ordering, setOrdering] = useState("-created_at");
  const [hasRelabeledPosicheck, setHasRelabeledPosicheck] = useState(false);
  const [choiceExamples, setChoiceExamples] = useState<{ [key: string]: ListResponse<ImageQuery> }>({});
  const [countExamples, setCountExamples] = useState<{ [key: string]: ListResponse<ImageQuery> }>({});

  const detectorDataLoading = useAppSelector(isFetchingDetector);
  const dispatch = useAppDispatch();

  const { state } = useLocation();
  const explorePage = isExplorePage(pageContext);

  const detectorId = detector?.id;
  const detectorMode = detector?.mode;

  const maxImageQueries = columns * MAX_ROWS_TO_FETCH;
  const imageQueriesType = areExamples ? "examples" : "answered-v2";
  const imageQueriesLabelField = areExamples ? "label" : "answer";
  const imageQueriesPageSizeField = areExamples ? "pageSize" : "limit";

  const {
    data: detectorPageImageQueries,
    error: errorDetectorPageImageQueries,
    isFetching: loadingDetectorPageImageQueries,
    refetch: refetchDetectorPageImageQueries,
  } = useGetImageQueries({
    params: { detectorId, pageSize: 12 },
    options: { enabled: explorePage, structuralSharing: false },
  });

  const {
    data: imageQueriesFlagged,
    error: errorImageQueriesFlagged,
    isFetching: loadingImageQueriesFlagged,
    refetch: refetchImageQueriesFlagged,
  } = useGetImageQueries({
    params: { detectorId, pageSize: 20, type: "flagged", ordering },
  });

  const {
    data: unclearImageQueries,
    error: errorUnclearImageQueries,
    isFetching: loadingUnclearImageQueries,
    refetch: getUnclearImageQueries,
  } = useGetImageQueries({
    params: {
      type: imageQueriesType,
      detectorId,
      [imageQueriesPageSizeField]: maxImageQueries,
      [imageQueriesLabelField]: STATUS.FLAGGED,
    },
    options: { enabled: !explorePage },
  });

  const {
    data: yesAnsweredImageQueries,
    error: errorYesAnsweredImageQueries,
    isFetching: loadingYesAnsweredImageQueries,
    refetch: getYesAnsweredImageQueries,
  } = useGetImageQueries({
    params: {
      type: imageQueriesType,
      detectorId,
      [imageQueriesPageSizeField]: maxImageQueries,
      [imageQueriesLabelField]: STATUS.YES_ANSWERED,
    },
    options: { enabled: DetectorModes.BINARY === detectorMode && !explorePage },
  });

  const {
    data: noAnsweredImageQueries,
    error: errorNoAnsweredImageQueries,
    isFetching: loadingNoAnsweredImageQueries,
    refetch: getNoAnsweredImageQueries,
  } = useGetImageQueries({
    params: {
      type: imageQueriesType,
      detectorId,
      [imageQueriesPageSizeField]: maxImageQueries,
      [imageQueriesLabelField]: STATUS.NO_ANSWERED,
    },
    options: { enabled: DetectorModes.BINARY === detectorMode && !explorePage },
  });

  // Use useFetchCustom for dynamic multichoice queries (needs to accept runtime parameters)
  // Disabled when detector mode is not MULTICHOICE by setting URL to undefined
  const multichoiceUrl =
    DetectorModes.MULTICHOICE === detectorMode && !explorePage
      ? constructImageQueriesUrl({
          type: imageQueriesType,
          predictorId: detectorId,
          [imageQueriesPageSizeField]: maxImageQueries,
        })
      : undefined;

  const {
    get: getMultichoiceAnsweredImageQueries,
    error: errorMultichoiceAnsweredImageQueries,
    loading: loadingMultichoiceAnsweredImageQueries,
  } = useFetchCustom<ListResponse<ImageQuery>>(multichoiceUrl, {
    cachePolicy: CachePolicies.NO_CACHE,
  });

  // Use useFetchCustom for dynamic count queries (needs to accept runtime parameters)
  // Disabled when detector mode is not COUNT by setting URL to undefined
  const countUrl =
    DetectorModes.COUNT === detectorMode && !explorePage
      ? constructImageQueriesUrl({
          type: imageQueriesType,
          predictorId: detectorId,
          [imageQueriesPageSizeField]: maxImageQueries,
        })
      : undefined;

  const {
    get: getCountAnsweredImageQueries,
    error: errorCountAnsweredImageQueries,
    loading: loadingCountAnsweredImageQueries,
  } = useFetchCustom<ListResponse<ImageQuery>>(countUrl, {
    cachePolicy: CachePolicies.NO_CACHE,
  });

  const {
    data: noTextImageQueries,
    error: errorNoTextImageQueries,
    isFetching: loadingNoTextImageQueries,
    refetch: getNoTextImageQueries,
  } = useGetImageQueries({
    params: {
      type: imageQueriesType,
      detectorId,
      [imageQueriesPageSizeField]: maxImageQueries,
      [imageQueriesLabelField]: STATUS.NO_TEXT,
    },
    options: { enabled: DetectorModes.TEXT_RECOGNITION === detectorMode && !explorePage },
  });

  const {
    data: hasTextImageQueries,
    error: errorHasTextImageQueries,
    isFetching: loadingHasTextImageQueries,
    refetch: getHasTextImageQueries,
  } = useGetImageQueries({
    params: {
      type: imageQueriesType,
      detectorId,
      [imageQueriesPageSizeField]: maxImageQueries,
      [imageQueriesLabelField]: STATUS.HAS_TEXT,
    },
    options: { enabled: DetectorModes.TEXT_RECOGNITION === detectorMode && !explorePage },
  });

  const {
    data: hasBoxesImageQueries,
    error: errorHasBoxesImageQueries,
    isFetching: loadingHasBoxesImageQueries,
    refetch: getHasBoxesImageQueries,
  } = useGetImageQueries({
    params: {
      type: imageQueriesType,
      detectorId,
      [imageQueriesPageSizeField]: maxImageQueries,
      [imageQueriesLabelField]: STATUS.BOUNDING_BOX,
    },
    options: { enabled: DetectorModes.BOUNDING_BOX === detectorMode && !explorePage },
  });

  const {
    data: noBoxesImageQueries,
    error: errorNoBoxesImageQueries,
    isFetching: loadingNoBoxesImageQueries,
    refetch: getNoBoxesImageQueries,
  } = useGetImageQueries({
    params: {
      type: imageQueriesType,
      detectorId,
      [imageQueriesPageSizeField]: maxImageQueries,
      [imageQueriesLabelField]: STATUS.NO_OBJECTS,
    },
    options: { enabled: DetectorModes.BOUNDING_BOX === detectorMode && !explorePage },
  });

  const {
    data: greaterThanMaxImageQueries,
    error: errorGreaterThanMaxImageQueries,
    isFetching: loadingGreaterThanMaxImageQueries,
    refetch: getGreaterThanMaxImageQueries,
  } = useGetImageQueries({
    params: {
      type: imageQueriesType,
      detectorId,
      [imageQueriesPageSizeField]: maxImageQueries,
      [imageQueriesLabelField]: STATUS.GREATER_THAN_MAX,
    },
    options: { enabled: DetectorModes.BOUNDING_BOX === detectorMode && !explorePage },
  });

  const relabeledPosicheckUrl = `${JANZU_URL}device-api/v1/image-queries/${relabeledPosicheckData}`;
  const { get: getRelabeledPosicheck, data: relabeledPosicheck } = useFetchCustom<ImageQuery>(relabeledPosicheckUrl, {
    cachePolicy: CachePolicies.NO_CACHE,
  });

  const isPosicheckLoaded = !(
    loadingImageQueriesFlagged ||
    loadingYesAnsweredImageQueries ||
    loadingNoAnsweredImageQueries ||
    loadingDetectorPageImageQueries ||
    loadingUnclearImageQueries ||
    loadingNoTextImageQueries ||
    loadingHasTextImageQueries ||
    loadingMultichoiceAnsweredImageQueries ||
    loadingCountAnsweredImageQueries ||
    loadingHasBoxesImageQueries ||
    loadingNoBoxesImageQueries ||
    loadingGreaterThanMaxImageQueries
  );

  const isError =
    errorImageQueriesFlagged ||
    errorYesAnsweredImageQueries ||
    errorNoAnsweredImageQueries ||
    errorNoTextImageQueries ||
    errorHasTextImageQueries ||
    errorHasBoxesImageQueries ||
    errorNoBoxesImageQueries ||
    errorGreaterThanMaxImageQueries;

  const handleChange = (event: React.SyntheticEvent, newValue: TabChoices) => {
    setSelectedTab(newValue);
  };

  const handleChangeOrdering = (value: string) => {
    setOrdering(value);
  };

  const onRefetch = useCallback(async (): Promise<void> => {
    if (!detectorId) return;

    dispatch(fetchPosicheck({ isFetchingPosicheck: true }));

    // Flagged images
    refetchImageQueriesFlagged();
    if (explorePage) {
      // Explore page specific query
      refetchDetectorPageImageQueries();
    } else {
      // Fetch unclear images
      getUnclearImageQueries();
      // Fetch multichoice examples
      if (detectorMode === DetectorModes.MULTICHOICE) {
        // Handle multi-choice queries separately so they don't block other data
        detector.mode_configuration?.class_names?.forEach((className) => {
          // Use URLSearchParams to properly encode the parameter
          const params = new URLSearchParams();
          params.append(imageQueriesLabelField, className);
          getMultichoiceAnsweredImageQueries(`&${params.toString()}`).then(
            (data) => {
              setChoiceExamples((prev) => ({ ...prev, [className]: data }));
            }
          );
        });
      } else if (detectorMode === DetectorModes.COUNT) {
        const maxCount = detector.mode_configuration?.max_count;
        if (maxCount) {
          if (!areExamples) {
            for (let i = 0; i <= maxCount; i += 1) {
              const params = new URLSearchParams();
              params.append('answer', i.toString());
              getCountAnsweredImageQueries(`&${params.toString()}`).then((data) => {
                setCountExamples((prev) => ({ ...prev, [i]: data }));
              });
            }
            // Examples for > max_count
            const greaterThanParams = new URLSearchParams();
            greaterThanParams.append('answer', `>${maxCount}`);
            getCountAnsweredImageQueries(`&${greaterThanParams.toString()}`).then((data) => {
              setCountExamples((prev) => ({ ...prev, GREATER_THAN_MAX: data }));
            });
          } else {
            const countParams = new URLSearchParams();
            countParams.append('label', 'COUNT');
            getCountAnsweredImageQueries(`&${countParams.toString()}`).then((data) => {
              setCountExamples((prev) => ({ ...prev, COUNT: data }));
            });
          }
        }
      } else if (detectorMode === DetectorModes.TEXT_RECOGNITION) {
        // Handle text recognition mode queries
        getNoTextImageQueries();
        getHasTextImageQueries();
      } else if (detectorMode === DetectorModes.BOUNDING_BOX) {
        getHasBoxesImageQueries();
        getNoBoxesImageQueries();
        getGreaterThanMaxImageQueries();
      } else {
        // Default case for yes/no answered
        getYesAnsweredImageQueries();
        getNoAnsweredImageQueries();
      }
    }

    dispatch(fetchPosicheck({ isFetchingPosicheck: false }));
  }, [
    detectorId,
    detectorMode,
    detector?.mode_configuration?.class_names,
    detector?.mode_configuration?.max_count,
    imageQueriesLabelField,
    areExamples,
    explorePage,
    dispatch,
    refetchImageQueriesFlagged,
    getMultichoiceAnsweredImageQueries,
    getUnclearImageQueries,
    refetchDetectorPageImageQueries,
    getYesAnsweredImageQueries,
    getNoAnsweredImageQueries,
    getCountAnsweredImageQueries,
    getNoTextImageQueries,
    getHasTextImageQueries,
    getHasBoxesImageQueries,
    getNoBoxesImageQueries,
    getGreaterThanMaxImageQueries,
  ]);

  useEffect(() => {
    // location state is preserved between refreshes and so the relabeled animation triggers every time
    // that the page is refreshed. ideally there would be some way to know if it triggered yet, or
    // possibly a solution would be to find a way to clear just the relabeledPosicheckId portion of
    // the navigation state and not the rest of it (I don't know if this is possible or not)
    if (state !== null) {
      const { relabeledPosicheckId } = state as { relabeledPosicheckId?: string };
      if (relabeledPosicheckId) {
        setSelectedTab(TabChoices.Flagged);
        if (relabeledPosicheckId !== "tab") {
          setRelabeledPosicheckData(relabeledPosicheckId);
        }
      }
    }
  }, [state]);

  useEffect(() => {
    // Initial fetch on mount for count mode examples
    if ((detectorMode === DetectorModes.COUNT || detectorMode === DetectorModes.MULTICHOICE) && detectorId) {
      onRefetch();
    }
  }, [detectorId, detectorMode, onRefetch]);

  useEffect(() => {
    const refreshTime = !explorePage ? DETECTOR_DETAILS_REFRESH_TIME : EXPLORE_PAGE_REFRESH_TIME;
    const canRefresh = count >= refreshTime;
    const shouldRefresh = isPosicheckLoaded && !detectorDataLoading && canRefresh;

    if (shouldRefresh) {
      onRefetch();
    }
  }, [count, explorePage, isPosicheckLoaded, detectorDataLoading, onRefetch]);

  useEffect(() => {
    // If an error occurs it will set the requestFailed state to true
    // which will stop refreshing the data
    if (isError) dispatch(setRequestFailed({ requestFailed: true }));
  }, [isError, dispatch]);

  useEffect(() => {
    if (relabeledPosicheckData) {
      getRelabeledPosicheck();
    }
  }, [getRelabeledPosicheck, relabeledPosicheckData]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (relabeledPosicheck) {
      setHasRelabeledPosicheck(true);
      timer = setTimeout(() => {
        setHasRelabeledPosicheck(false);
      }, 2000);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [relabeledPosicheck]);

  useEffect(() => {
    if (imageQueriesFlagged && !errorImageQueriesFlagged) {
      const flaggedCount = imageQueriesFlagged.count;
      setFlaggedCount(flaggedCount);
    } else {
      setFlaggedCount(0);
    }
  }, [imageQueriesFlagged, errorImageQueriesFlagged, setFlaggedCount]);

  useEffect(() => {
    if (!hasRelabeledPosicheck) {
      setFlaggedCount(imageQueriesFlagged?.results ? imageQueriesFlagged.count : 0);
    }
  }, [hasRelabeledPosicheck, setFlaggedCount, imageQueriesFlagged]);

  return {
    detectorPageImageQueries,
    errorDetectorPageImageQueries,
    imageQueriesFlagged,
    errorImageQueriesFlagged,
    refetchImageQueriesFlagged,
    errorCountAnsweredImageQueries,
    errorMultichoiceAnsweredImageQueries,
    noTextImageQueries,
    errorNoTextImageQueries,
    hasTextImageQueries,
    errorHasTextImageQueries,
    unclearImageQueries,
    errorUnclearImageQueries,
    yesAnsweredImageQueries,
    errorYesAnsweredImageQueries,
    noAnsweredImageQueries,
    errorNoAnsweredImageQueries,
    hasBoxesImageQueries,
    errorHasBoxesImageQueries,
    noBoxesImageQueries,
    errorNoBoxesImageQueries,
    greaterThanMaxImageQueries,
    errorGreaterThanMaxImageQueries,
    relabeledPosicheck,
    selectedTab,
    choiceExamples,
    countExamples,
    flaggedCount,
    ordering,
    hasRelabeledPosicheck,
    isExplorePage: explorePage,
    handleChange,
    handleChangeOrdering,
    onRefetch,
  };
}

export default useImageQueryPreview;
